import React, { useEffect, useState } from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi, discoveryApiRef, identityApiRef } from '@backstage/core-plugin-api';
import { InfoCard, Progress } from '@backstage/core-components';
import {
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  makeStyles,
} from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';
import CancelIcon from '@material-ui/icons/Cancel';

const useStyles = makeStyles(theme => ({
  statusSuccess: { color: theme.palette.success.main },
  statusFailed: { color: theme.palette.error.main },
  statusRunning: { color: theme.palette.info.main },
  statusCancelled: { color: theme.palette.text.secondary },
  link: { color: theme.palette.primary.main, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } },
  table: { fontSize: 12 },
  cell: { padding: theme.spacing(0.75, 1) },
  noData: { padding: theme.spacing(2) },
}));

interface BuildRun {
  id: number;
  buildNumber: string;
  pipelineName: string;
  status: string;   // 'completed' | 'inProgress' | 'notStarted' | 'cancelling'
  result?: string;  // 'succeeded' | 'failed' | 'canceled' | 'partiallySucceeded'
  sourceBranch: string;
  sourceVersion?: string;
  startTime?: string;
  finishTime?: string;
  webUrl: string;
}

interface BuildDetails {
  id: number;
  pipelineName: string;
  sourceVersion?: string;
  jobs: Array<{
    id: string;
    type: string;
    name: string;
    state: string;
    result?: string;
    logId?: number;
  }>;
  artifacts: Array<{ name: string; resourceType?: string }>;
}

function StatusIcon({ status, result }: { status: string; result?: string }) {
  const classes = useStyles();
  if (status === 'inProgress' || status === 'notStarted') {
    return <HourglassEmptyIcon fontSize="small" className={classes.statusRunning} />;
  }
  if (status === 'cancelling' || result === 'canceled') {
    return <CancelIcon fontSize="small" className={classes.statusCancelled} />;
  }
  if (result === 'succeeded' || result === 'partiallySucceeded') {
    return <CheckCircleIcon fontSize="small" className={classes.statusSuccess} />;
  }
  if (result === 'failed') {
    return <ErrorIcon fontSize="small" className={classes.statusFailed} />;
  }
  return <HourglassEmptyIcon fontSize="small" className={classes.statusRunning} />;
}

function resultLabel(status: string, result?: string): string {
  if (status === 'inProgress') return 'Running';
  if (status === 'notStarted') return 'Queued';
  if (status === 'cancelling') return 'Cancelling';
  if (result === 'succeeded') return 'Succeeded';
  if (result === 'partiallySucceeded') return 'Partial';
  if (result === 'failed') return 'Failed';
  if (result === 'canceled') return 'Cancelled';
  return status;
}

function formatDuration(start?: string, finish?: string): string {
  if (!start || !finish) return '—';
  const ms = new Date(finish).getTime() - new Date(start).getTime();
  const mins = Math.floor(ms / 60000);
  const secs = Math.floor((ms % 60000) / 1000);
  return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
}

function formatDate(dateStr?: string): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

/**
 * Shows recent Azure DevOps pipeline runs for the entity.
 * Reads both application pipeline names from entity metadata.
 * Calls GET /api/azure-apps/components/pipeline-runs to fetch runs.
 */
export function AzureAdoCicdCard() {
  const classes = useStyles();
  const { entity } = useEntity();
  const entityRef = `${entity.kind.toLowerCase()}:${entity.metadata.namespace ?? 'default'}/${entity.metadata.name}`;
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);

  const meta = entity.metadata as Record<string, any>;
  const organization = meta.adoOrganization as string | undefined;
  const project = meta.adoProject as string | undefined;
  const pipelineName = meta.adoPipelineName as string | undefined;
  const environmentPipelineName = meta.adoDltPipelineName as string | undefined;
  const pipelineNames = [pipelineName, environmentPipelineName].filter(Boolean) as string[];

  const [runs, setRuns] = useState<BuildRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [retrying, setRetrying] = useState<number>();
  const [requestingRollback, setRequestingRollback] = useState<number>();
  const [details, setDetails] = useState<BuildDetails>();
  const [log, setLog] = useState<{ text: string; truncated: boolean }>();
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    let cancelled = false;
    if (!organization || !project || pipelineNames.length === 0) {
      setLoading(false);
      return;
    }
    const load = async () => {
      try {
        const baseUrl = await discoveryApi.getBaseUrl('azure-apps');
        const { token } = await identityApi.getCredentials();
        const params = new URLSearchParams({
          entityRef,
          organization,
          project,
          pipelineNames: pipelineNames.join(','),
          top: '30',
        });
        const res = await fetch(`${baseUrl}/components/pipeline-runs?${params}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error((await res.json()).error ?? res.statusText);
        const data: BuildRun[] = await res.json();
        if (!cancelled) {
          setRuns(data);
          setError('');
        }
      } catch (e: any) {
        if (!cancelled) setError(e.message ?? 'Failed to load pipeline runs');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    const timer = window.setInterval(load, 15000);
    return () => { cancelled = true; window.clearInterval(timer); };
  }, [entityRef, organization, project, pipelineNames.join(','), discoveryApi, identityApi, refresh]);

  const retryRun = async (run: BuildRun) => {
    try {
      setRetrying(run.id);
      setError('');
      const baseUrl = await discoveryApi.getBaseUrl('azure-apps');
      const { token } = await identityApi.getCredentials();
      const res = await fetch(`${baseUrl}/pipelines/${run.id}/retry`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ entityRef, organization, project }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error ?? res.statusText);
      setRefresh(value => value + 1);
    } catch (e: any) {
      setError(e.message ?? `Failed to retry run ${run.id}`);
    } finally {
      setRetrying(undefined);
    }
  };

  const loadDetails = async (run: BuildRun) => {
    try {
      setError('');
      setLog(undefined);
      const baseUrl = await discoveryApi.getBaseUrl('azure-apps');
      const { token } = await identityApi.getCredentials();
      const params = new URLSearchParams({ entityRef, organization: organization!, project: project! });
      const res = await fetch(`${baseUrl}/pipelines/${run.id}/details?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error ?? res.statusText);
      setDetails(body);
    } catch (e: any) {
      setError(e.message ?? `Failed to load run ${run.id}`);
    }
  };

  const requestRollback = async (run: BuildRun) => {
    if (!run.sourceVersion) {
      setError(`Run ${run.id} does not expose a source revision`);
      return;
    }
    const reason = window.prompt(
      `Explain why ${run.sourceVersion.slice(0, 12)} should be restored (minimum 10 characters):`,
    );
    if (!reason) return;
    try {
      setRequestingRollback(run.id);
      setError('');
      const baseUrl = await discoveryApi.getBaseUrl('arta-governance');
      const { token } = await identityApi.getCredentials();
      const res = await fetch(`${baseUrl}/deployment-operations`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entityRef,
          provider: 'azure-devops',
          action: 'rollback',
          sourceRunId: String(run.id),
          targetRevision: run.sourceVersion,
          reason,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error ?? res.statusText);
      window.alert(`Rollback request ${body.id} is awaiting platform approval.`);
    } catch (e: any) {
      setError(e.message ?? `Failed to request rollback to run ${run.id}`);
    } finally {
      setRequestingRollback(undefined);
    }
  };

  const loadLog = async (logId: number) => {
    if (!details) return;
    try {
      setError('');
      const baseUrl = await discoveryApi.getBaseUrl('azure-apps');
      const { token } = await identityApi.getCredentials();
      const params = new URLSearchParams({ entityRef, organization: organization!, project: project! });
      const res = await fetch(`${baseUrl}/pipelines/${details.id}/logs/${logId}?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error ?? res.statusText);
      setLog(body);
    } catch (e: any) {
      setError(e.message ?? `Failed to load log ${logId}`);
    }
  };

  const adoProjectUrl = organization && project
    ? `https://dev.azure.com/${organization}/${encodeURIComponent(project)}/_build`
    : undefined;

  const title = (
    <span>
      Pipeline Runs
      {pipelineNames.length > 0 && (
        <Typography component="span" variant="body2" color="textSecondary" style={{ marginLeft: 8 }}>
          Application pipelines
        </Typography>
      )}
      {adoProjectUrl && (
        <a
          href={adoProjectUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={classes.link}
          style={{ marginLeft: 12, fontSize: 12 }}
        >
          View in Azure DevOps ↗
        </a>
      )}
    </span>
  );

  if (!organization || !project || !pipelineName) {
    return (
      <InfoCard title="Pipeline Runs">
        <Typography variant="body2" color="textSecondary">
          ADO pipeline metadata not found on this entity (adoOrganization, adoProject, adoPipelineName).
        </Typography>
      </InfoCard>
    );
  }

  return (
    <InfoCard title={title} noPadding>
      {loading && <Progress />}
      {!loading && error && (
        <Typography variant="body2" color="error" style={{ padding: 16 }}>
          Error: {error}
        </Typography>
      )}
      {!loading && !error && runs.length === 0 && (
        <Typography variant="body2" color="textSecondary" className={classes.noData}>
          No pipeline runs found.
        </Typography>
      )}
      {!loading && !error && runs.length > 0 && (
        <Table size="small" className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell className={classes.cell}>Status</TableCell>
              <TableCell className={classes.cell}>Build</TableCell>
              <TableCell className={classes.cell}>Pipeline</TableCell>
              <TableCell className={classes.cell}>Branch</TableCell>
              <TableCell className={classes.cell}>Started</TableCell>
              <TableCell className={classes.cell}>Duration</TableCell>
              <TableCell className={classes.cell} align="right">Operations</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {runs.map(run => (
              <TableRow key={run.id} hover>
                <TableCell className={classes.cell}>
                  <Chip
                    icon={<StatusIcon status={run.status} result={run.result} />}
                    label={resultLabel(run.status, run.result)}
                    size="small"
                    style={{ height: 22, fontSize: 11 }}
                  />
                </TableCell>
                <TableCell className={classes.cell}>
                  <a href={run.webUrl} target="_blank" rel="noopener noreferrer" className={classes.link}>
                    #{run.buildNumber}
                  </a>
                </TableCell>
                <TableCell className={classes.cell}>
                  <Typography variant="caption">{run.pipelineName}</Typography>
                </TableCell>
                <TableCell className={classes.cell}>
                  <Typography variant="caption">{run.sourceBranch}</Typography>
                </TableCell>
                <TableCell className={classes.cell}>
                  <Typography variant="caption">{formatDate(run.startTime)}</Typography>
                </TableCell>
                <TableCell className={classes.cell}>
                  <Typography variant="caption">
                    {run.status === 'inProgress' ? 'Running…' : formatDuration(run.startTime, run.finishTime)}
                  </Typography>
                </TableCell>
                <TableCell className={classes.cell} align="right">
                  <Button size="small" onClick={() => loadDetails(run)}>Details</Button>
                  {run.result === 'succeeded' && run.sourceVersion && (
                    <Button
                      size="small"
                      disabled={requestingRollback === run.id}
                      onClick={() => requestRollback(run)}
                    >
                      {requestingRollback === run.id ? 'Requesting…' : 'Request rollback'}
                    </Button>
                  )}
                  {run.status === 'completed' && ['failed', 'canceled', 'partiallySucceeded'].includes(run.result ?? '') && (
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={retrying === run.id}
                      onClick={() => retryRun(run)}
                    >
                      {retrying === run.id ? 'Retrying…' : 'Retry failed run'}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {details && (
        <div style={{ padding: 16 }}>
          <Typography variant="subtitle2">
            Run #{details.id} jobs and outputs
          </Typography>
          {details.sourceVersion && (
            <Typography variant="caption" color="textSecondary">
              Commit {details.sourceVersion.slice(0, 12)}
            </Typography>
          )}
          {details.jobs.map(job => (
            <div key={job.id} style={{ marginTop: 10 }}>
              <Chip size="small" label={`${job.type}: ${job.name} — ${job.result ?? job.state}`} />{' '}
              {job.logId && (
                <Button size="small" onClick={() => loadLog(job.logId!)}>View log</Button>
              )}
            </div>
          ))}
          {details.artifacts.length > 0 && (
            <Typography variant="caption" display="block" color="textSecondary" style={{ marginTop: 10 }}>
              Outputs: {details.artifacts.map(item => item.name).join(', ')}
            </Typography>
          )}
          {log && (
            <div style={{ marginTop: 12 }}>
              <Typography variant="caption" color="textSecondary">
                Log{log.truncated ? ' (tail, truncated)' : ''}
              </Typography>
              <pre style={{ maxHeight: 360, overflow: 'auto', whiteSpace: 'pre-wrap', background: '#111', color: '#eee', padding: 12 }}>
                {log.text}
              </pre>
            </div>
          )}
        </div>
      )}
    </InfoCard>
  );
}
